import { useCallback } from "react";
import { Collapse, Typography } from "@mui/material";
import DoctorCard from "../../../components/dashboard/doctorCard/DoctorCard";
import InsuranceCard from "../../../components/dashboard/InsuranceCard/InsuranceCard";
import { OVERALL_CARDS_LABELS, OVERALL_CARDS_TYPE } from "../../../types/overall.type";
import { OVERALL_PAGE_SUB_TYPE, PAGE_TYPE } from "../../../types/pages.type";
import DayCardList from "../../hospital/components/dayCardList/DayCardList";
import FloorCardList from "../../hospital/components/floorCardList/FloorCardList";
import NinetyDaysAnalysis from "../../hospital/components/ninetyDaysAnalysis/NinetyDaysAnalysis";
import DynamicDataCardList from "../../hospital/components/DynamicDataCardList/DynamicDataCardList";
import {
	findHospitalDynamicCardByKey,
	HeaderFilterCardShell,
	PanelWrap,
} from "../../hospital/components/HospitalHeaderCardFilterPanel";

function OverallHeaderDynamicFilterBody({
	ele,
	accessor,
	cardFilter,
	filterListData,
	filterListDataComparison,
	handleToggle,
	handleToggleAll,
	filter,
	filterComparison,
	loadingBoth,
	dbData,
	dbDataComparison,
	percentageAgainst,
	averageCensusComparison,
	projectionDays,
	getPriorityNumber,
	transferType,
	lockedTotalBy,
	filterTotal,
}) {
	const cardSelectedItems = cardFilter?.[accessor] ?? [];
	const cardID = filterListData?.[accessor]?.[0]?.id;
	const isSelectedTotalCard = cardSelectedItems.indexOf(cardID) !== -1;
	const isCustomTab = Boolean(ele?.isCustomTab);
	const question = ele?.question;

	const handleCardClick = useCallback(
		async (_titleParam = null, uniqueKey = null) => {
			const compositeKey = uniqueKey && accessor ? `${accessor}__${uniqueKey}` : null;
			const comparisonItem = filterListDataComparison?.[accessor]?.[0];
			await handleToggle({
				item: filterListData?.[accessor]?.[0],
				itemComparison: comparisonItem,
				type: accessor,
				isChecked: isSelectedTotalCard,
				cardTitle: ele.title || ele.label || ele.tableLabel,
				question: {
					isCustom: true,
					customQuestionInputType: question?.customQuestionInputType,
					isCustomTab: false,
					cardTitle: ele.title || ele.label || ele.tableLabel,
					customTab: ele.type,
					accessor,
					compositeKey,
					uniqueKey,
				},
				isCustomTab: false,
			});
		},
		[accessor, ele, filterListData, filterListDataComparison, handleToggle, isSelectedTotalCard, question]
	);

	return (
		<DynamicDataCardList
			compactForHeaderPopover
			disableInteractions={false}
			totalTransfers={dbData.total}
			totalTransfersComparison={dbDataComparison?.total}
			filterListData={filterListData}
			filterListDataComparison={filterListDataComparison}
			question={ele}
			cardTitle={ele.title || ele.label || ele.tableLabel}
			data={filterListData?.[accessor] ?? []}
			dataComparison={filterListDataComparison?.[accessor] ?? []}
			handleToggle={handleToggle}
			handleToggleAll={handleToggleAll}
			selectedItem={cardFilter?.[accessor]}
			spacialSelectedItem={cardFilter?.[`${accessor}_spacial`]}
			type={accessor}
			filter={filter}
			loading={loadingBoth}
			filterComparison={filterComparison}
			page={PAGE_TYPE.OVERALL}
			averageCensus={percentageAgainst}
			projectionDays={projectionDays}
			isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
			priorityNumber={getPriorityNumber(accessor)}
			transferType={transferType}
			lockedTotalBy={lockedTotalBy}
			isCustomTab={isCustomTab}
			filterTotal={filterTotal}
			handleCardClick={handleCardClick}
			isSelectedTotalCard={isSelectedTotalCard}
			cardFilter={cardFilter}
			isDisabled={false}
			admissionCompareAgainst={null}
			admissionCompareAgainstComparison={null}
			reverseColorsAdmissionPage={false}
			averageCensusComparison={averageCensusComparison}
		/>
	);
}

/**
 * Interactive filter popovers for Overall dashboard header chips (same handlers/data as main cards).
 */
export default function OverallHeaderCardFilterPanel({
	dataKey,
	handleToggle,
	handleToggleAll,
	filterListData,
	filterListDataComparison,
	cardFilter,
	filter,
	filterComparison,
	loading,
	loadingComparison,
	projectionDays,
	filterTotal,
	dbData,
	dbDataComparison,
	transferType,
	lockedTotalBy,
	getPriorityNumber,
	comparingAgainstDifferentFacility,
	dynamicCards = [],
	overallDynamicTabDataByAccessor = {},
	percentageAgainst,
	shouldReverseOverallColors,
	headerFilterPopoverOpen,
}) {
	const loadingBoth = loading || loadingComparison;
	const averageCensusComparison = dbDataComparison?.censusAverage;
	const floorTransferType = Array.isArray(transferType) ? transferType[0] || "" : transferType || "";

	const panelContent = (() => {
		switch (dataKey) {
		case OVERALL_CARDS_TYPE.INSURANCE_DATA:
			return (
				<HeaderFilterCardShell
					title={OVERALL_CARDS_LABELS.insuranceData}
					bodySx={{ p: 0, pt: 0.5, overflow: "visible" }}
				>
					<InsuranceCard
						cardTitle={OVERALL_CARDS_LABELS.insuranceData}
						isCompareColor={transferType === OVERALL_PAGE_SUB_TYPE.TOTAL}
						height={320}
						data={filterListData.insuranceData}
						filter={filter}
						dataComparison={filterListDataComparison?.insuranceData}
						handleToggle={handleToggle}
						handleToggleAll={handleToggleAll}
						selectedItem={cardFilter.insuranceData}
						type="insuranceData"
						loading={loadingBoth}
						page={PAGE_TYPE.OVERALL}
						filterComparison={filterComparison}
						averageCensus={percentageAgainst}
						averageCensusComparison={averageCensusComparison}
						reverseColors={shouldReverseOverallColors}
						projectionDays={projectionDays}
						isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
						priorityNumber={getPriorityNumber(OVERALL_CARDS_TYPE.INSURANCE_DATA)}
						transferType={transferType}
						lockedTotalBy={lockedTotalBy}
						headerFilterPopover
						hideProgressBar
						disableInteractions={false}
						isMobile={false}
					/>
				</HeaderFilterCardShell>
			);

		case OVERALL_CARDS_TYPE.NINETY_DAYS_DATA:
			return (
				<PanelWrap title={OVERALL_CARDS_LABELS.ninetyDaysData}>
						<NinetyDaysAnalysis
							cardTitle="90 Days Analysis"
							handleToggle={handleToggle}
							loading={loadingBoth}
							selectedItem={cardFilter.ninetyDaysData}
							type="ninetyDaysData"
							data={filterListData.ninetyDaysData}
							comparisonData={filterListDataComparison?.ninetyDaysData}
							page={PAGE_TYPE.OVERALL}
							filterListData={filterListData}
							filterListDataComparison={filterListDataComparison}
							filter={filter}
							filterComparison={filterComparison}
							averageCensus={percentageAgainst}
							averageCensusComparison={averageCensusComparison}
							headerFilterPopover
							hideRadialSection
							reverseColors={shouldReverseOverallColors}
							projectionDays={projectionDays}
							filterTotal={filterTotal}
							isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
							priorityNumber={getPriorityNumber(OVERALL_CARDS_TYPE.NINETY_DAYS_DATA)}
							transferType={transferType}
							lockedTotalBy={lockedTotalBy}
							disableInteractions={false}
					/>
				</PanelWrap>
			);

		case OVERALL_CARDS_TYPE.DOCTOR_DATA:
			return (
				<PanelWrap title={OVERALL_CARDS_LABELS.doctorData}>
						<DoctorCard
							cardTitle={OVERALL_CARDS_LABELS.doctorData}
							data={filterListData?.doctorData ?? []}
							comparisonData={filterListDataComparison?.doctorData}
							minimizable={true}
							handleToggle={handleToggle}
							handleToggleAll={handleToggleAll}
							selectedItem={cardFilter?.doctorData ?? []}
							type={OVERALL_CARDS_TYPE.DOCTOR_DATA}
							page={PAGE_TYPE.OVERALL}
							loading={loadingBoth}
							averageCensus={percentageAgainst}
							averageCensusComparison={averageCensusComparison}
							filter={filter}
							filterComparison={filterComparison}
							comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
							reverseColors={shouldReverseOverallColors}
							projectionDays={projectionDays}
							filterTotal={filterTotal}
							isComparingAgainstAvgCensus={Boolean(filterListData?.isComparingAgainstAvgCensus)}
							priorityNumber={getPriorityNumber(OVERALL_CARDS_TYPE.DOCTOR_DATA)}
							transferType={transferType}
							lockedTotalBy={lockedTotalBy}
							disableInteractions={false}
							isMobile={false}
							forHeaderPopover
					/>
				</PanelWrap>
			);

		case OVERALL_CARDS_TYPE.FLOORS_DATA:
			return (
				<PanelWrap title={OVERALL_CARDS_LABELS.floorsData}>
						<FloorCardList
							cardTitle={OVERALL_CARDS_LABELS.floorsData}
							isCompareColor={transferType === OVERALL_PAGE_SUB_TYPE.TOTAL}
							data={filterListData.floorsData}
							dataComparison={filterListDataComparison?.floorsData}
							handleToggle={handleToggle}
							handleToggleAll={handleToggleAll}
							selectedItem={cardFilter.floorsData}
							type="floorsData"
							filter={filter}
							loading={loadingBoth}
							filterComparison={filterComparison}
							page={PAGE_TYPE.OVERALL}
							isPercentageShow={
								transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING ||
								transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING
							}
							averageCensusComparison={averageCensusComparison}
							averageCensus={percentageAgainst}
							reverseColors={shouldReverseOverallColors}
							comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
							projectionDays={projectionDays}
							isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
							priorityNumber={getPriorityNumber(OVERALL_CARDS_TYPE.FLOORS_DATA)}
							transferType={floorTransferType}
							lockedTotalBy={lockedTotalBy}
							disableInteractions={false}
					/>
				</PanelWrap>
			);

		case OVERALL_CARDS_TYPE.DAYS_DATA:
			return (
				<PanelWrap title={OVERALL_CARDS_LABELS.daysData}>
						<DayCardList
							cardTitle={OVERALL_CARDS_LABELS.daysData}
							data={filterListData.daysData}
							dataComparison={filterListDataComparison?.daysData}
							handleToggle={handleToggle}
							selectedItem={cardFilter.daysData}
							filter={{ ...filter, transferType }}
							loading={loadingBoth}
							filterComparison={filterComparison}
							type={OVERALL_CARDS_TYPE.DAYS_DATA}
							page={PAGE_TYPE.OVERALL}
							averageCensusComparison={averageCensusComparison}
							averageCensus={percentageAgainst}
							reverseColorsAdmissionPage={shouldReverseOverallColors}
							comparingAgainstDifferentFacility={comparingAgainstDifferentFacility}
							projectionDays={projectionDays}
							filterTotal={filterTotal}
							isComparingAgainstAvgCensus={filterListData.isComparingAgainstAvgCensus}
							priorityNumber={getPriorityNumber(OVERALL_CARDS_TYPE.DAYS_DATA)}
							transferType={transferType}
							lockedTotalBy={lockedTotalBy}
							disableInteractions={false}
							isPercentageShow={
								transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING ||
								transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING
							}
					/>
				</PanelWrap>
			);

		default: {
			const dynamicEle =
				overallDynamicTabDataByAccessor[dataKey] || findHospitalDynamicCardByKey(dataKey, dynamicCards);
			if (dynamicEle) {
				const accessor = dynamicEle.accessor || dataKey;
				const dynamicTitle =
					dynamicEle.title || dynamicEle.label || dynamicEle.tableLabel || String(dataKey);
				return (
					<PanelWrap title={dynamicTitle}>
						<OverallHeaderDynamicFilterBody
							ele={dynamicEle}
							accessor={accessor}
							cardFilter={cardFilter}
							filterListData={filterListData}
							filterListDataComparison={filterListDataComparison}
							handleToggle={handleToggle}
							handleToggleAll={handleToggleAll}
							filter={filter}
							filterComparison={filterComparison}
							loadingBoth={loadingBoth}
							dbData={dbData}
							dbDataComparison={dbDataComparison}
							percentageAgainst={percentageAgainst}
							averageCensusComparison={averageCensusComparison}
							projectionDays={projectionDays}
							getPriorityNumber={getPriorityNumber}
							transferType={transferType}
							lockedTotalBy={lockedTotalBy}
							filterTotal={filterTotal}
					/>
				</PanelWrap>
				);
			}
			return (
				<PanelWrap title="Filter">
					<Typography variant="body2" sx={{ p: 1, color: "text.secondary", fontSize: 12 }}>
						This filter is not available in the header preview.
					</Typography>
				</PanelWrap>
			);
		}
	}
	})();

	if (headerFilterPopoverOpen === undefined) {
		return panelContent;
	}

	return (
		<Collapse orientation="vertical" in={headerFilterPopoverOpen} timeout={1000} unmountOnExit={false}>
			{panelContent}
		</Collapse>
	);
}
